import React, { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
} from '@mui/material';
import {
  Pause as PauseIcon,
  PlayArrow as ResumeIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useAccessControl } from '../../../hooks/useAccessControl';
import { RESOURCES, ACTIONS } from '../../../hooks/usePermissions';
import { ledgerAPI } from '../../../services/api';
import { formatAccountingAmount } from '../../../utils/formatters';
import { useAppSnackbar, useConfirmDialog } from '../../common/TableDesign';
import LedgerListPage from '../LedgerListPage';
import { AccountPicker, BankAccountPicker } from '../LedgerPickers';
import { ExpenseStatusChip, fmtDate, getErrorMessage } from './ExpensesShared';

const emptyForm = () => ({
  description: '', amount: '', expense_account_id: '', paid_from_account_id: '',
  interval_months: 1, start_date: new Date().toISOString().slice(0, 10), end_date: '',
});

// Recurring operational expenses (e.g. a monthly subscription) - generates
// a DRAFT expense each time it comes due, same run/pause/resume idiom as
// income/RevenueRecurringTab.js and the verified
// general-ledger/recurring-journals (Journals.js).
const ExpenseRecurringTab = () => {
  const { can } = useAccessControl();
  const canCreate = can(RESOURCES.LEDGER.EXPENSES, ACTIONS.CREATE);
  const canUpdate = can(RESOURCES.LEDGER.EXPENSES, ACTIONS.UPDATE);
  const canDelete = can(RESOURCES.LEDGER.EXPENSES, ACTIONS.DELETE);
  const { showSnackbar, SnackbarComponent } = useAppSnackbar();
  const { confirm, ConfirmDialogComponent } = useConfirmDialog();

  const [refreshKey, setRefreshKey] = useState(0);
  const [showCreate, setShowCreate] = useState(false);
  const [values, setValues] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);

  const reload = () => setRefreshKey((k) => k + 1);

  const submitCreate = async () => {
    if (!values.description.trim()) {
      showSnackbar('Description is required', 'error');
      return;
    }
    if (!(Number(values.amount) > 0)) {
      showSnackbar('Amount must be greater than zero', 'error');
      return;
    }
    if (!values.expense_account_id || !values.paid_from_account_id) {
      showSnackbar('Expense account and bank/cash account are required', 'error');
      return;
    }
    const payload = {
      description: values.description.trim(),
      amount: Number(values.amount),
      expense_account_id: Number(values.expense_account_id),
      paid_from_account_id: Number(values.paid_from_account_id),
      interval_months: Number(values.interval_months) || 1,
      start_date: values.start_date,
    };
    if (values.end_date) payload.end_date = values.end_date;
    try {
      setSaving(true);
      await ledgerAPI.createExpenseRecurring(payload);
      showSnackbar('Recurring expense template created', 'success');
      setShowCreate(false);
      setValues(emptyForm());
      reload();
    } catch (error) {
      showSnackbar(getErrorMessage(error), 'error');
    } finally {
      setSaving(false);
    }
  };

  const runDue = () => {
    confirm({
      title: 'Run due templates',
      message: 'Generate one DRAFT expense per due recurring template?',
      onConfirm: async () => {
        try {
          setRunning(true);
          await ledgerAPI.runExpenseRecurring({});
          showSnackbar('Due templates generated as DRAFT expenses', 'success');
          reload();
        } catch (error) {
          showSnackbar(getErrorMessage(error), 'error');
        } finally {
          setRunning(false);
        }
      },
    });
  };

  const togglePause = (r) => {
    const paused = String(r.status).toUpperCase() === 'PAUSED';
    confirm({
      title: paused ? 'Resume template' : 'Pause template',
      message: `${paused ? 'Resume' : 'Pause'} "${r.description}"?`,
      onConfirm: async () => {
        try {
          if (paused) await ledgerAPI.resumeExpenseRecurring(r.recurring_id);
          else await ledgerAPI.pauseExpenseRecurring(r.recurring_id);
          showSnackbar(`Template ${paused ? 'resumed' : 'paused'}`, 'success');
          reload();
        } catch (error) {
          showSnackbar(getErrorMessage(error), 'error');
        }
      },
    });
  };

  return (
    <>
      <LedgerListPage
        resource={RESOURCES.LEDGER.EXPENSES}
        title="Recurring Expenses"
        subtitle="Templates that generate a DRAFT expense each time they come due"
        addLabel="New Template"
        idKey="recurring_id"
        searchPlaceholder="Search description..."
        emptyMessage="No recurring expense templates found."
        refreshKey={refreshKey}
        fetchItems={(params) => ledgerAPI.getExpenseRecurring(params)}
        onAdd={canCreate ? () => { setValues(emptyForm()); setShowCreate(true); } : null}
        extraActions={canCreate ? [{ label: running ? 'Running...' : 'Run Due Now', variant: 'outlined', onClick: runDue }] : []}
        filters={[
          { key: 'status', label: 'Status', options: ['ACTIVE', 'PAUSED', 'COMPLETED'].map((s) => ({ label: s, value: s })) },
        ]}
        columns={[
          { label: 'Description', render: (r) => <strong>{r.description}</strong> },
          { label: 'Amount', render: (r) => formatAccountingAmount(r.amount), align: 'right' },
          { label: 'Every', render: (r) => `${r.interval_months || 1} mo`, hideOnMobile: true },
          { label: 'Next Run', render: (r) => fmtDate(r.next_run_date), hideOnMobile: true },
          { label: 'Status', render: (r) => <ExpenseStatusChip status={r.status} /> },
        ]}
        rowActions={(r) => {
          const paused = String(r.status).toUpperCase() === 'PAUSED';
          const actions = [];
          if (canUpdate) {
            actions.push({
              icon: paused ? ResumeIcon : PauseIcon, title: paused ? 'Resume' : 'Pause', color: 'primary',
              onClick: () => togglePause(r),
            });
          }
          if (canDelete) {
            actions.push({
              icon: DeleteIcon, title: 'Delete', color: 'error',
              onClick: () => confirm({
                title: 'Delete template',
                message: `Delete recurring template "${r.description}"? This cannot be undone.`,
                onConfirm: async () => {
                  try {
                    await ledgerAPI.deleteExpenseRecurring(r.recurring_id);
                    showSnackbar('Template deleted', 'success');
                    reload();
                  } catch (error) {
                    showSnackbar(getErrorMessage(error), 'error');
                  }
                },
              }),
            });
          }
          return actions;
        }}
      />

      <Dialog open={showCreate} onClose={() => setShowCreate(false)} maxWidth="sm" fullWidth>
        <DialogTitle>New Recurring Expense Template</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid size={12}>
              <TextField
                fullWidth size="small" label="Description" value={values.description}
                onChange={(e) => setValues((p) => ({ ...p, description: e.target.value }))}
                required
              />
            </Grid>
            <Grid size={6}>
              <TextField
                fullWidth size="small" type="number" label="Amount" value={values.amount}
                onChange={(e) => setValues((p) => ({ ...p, amount: e.target.value }))}
                inputProps={{ min: 0.01, step: 'any' }} required
              />
            </Grid>
            <Grid size={6}>
              <TextField
                fullWidth size="small" type="number" label="Every (months)" value={values.interval_months}
                onChange={(e) => setValues((p) => ({ ...p, interval_months: e.target.value }))}
                inputProps={{ min: 1, step: 1 }}
              />
            </Grid>
            <Grid size={6}>
              <TextField
                fullWidth size="small" type="date" label="Start Date" value={values.start_date}
                onChange={(e) => setValues((p) => ({ ...p, start_date: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={6}>
              <TextField
                fullWidth size="small" type="date" label="End Date (optional)" value={values.end_date}
                onChange={(e) => setValues((p) => ({ ...p, end_date: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={12}>
              <AccountPicker label="Expense account" value={values.expense_account_id} onChange={(id) => setValues((p) => ({ ...p, expense_account_id: id }))} required />
            </Grid>
            <Grid size={12}>
              <BankAccountPicker label="Paid From" value={values.paid_from_account_id} onChange={(id) => setValues((p) => ({ ...p, paid_from_account_id: id }))} required />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCreate(false)}>Cancel</Button>
          <Button variant="contained" onClick={submitCreate} disabled={saving}>
            {saving ? 'Saving...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {ConfirmDialogComponent}
      {SnackbarComponent}
    </>
  );
};

export default ExpenseRecurringTab;
